export interface RoomPrice {
  net: number;
  gross?: number;
}

export interface DayAvailability {
  date: string;
  rooms: {
    [categoryOptionId: string]: {
      name: string;
      pricing: {
        [date: string]: {
          double?: RoomPrice;
          single?: RoomPrice;
          triple?: RoomPrice;
        };
      };
    };
  };
}

export interface WeekendPricingSuggestion {
  avgFridayPrice: number;
  avgSaturdayPrice: number;
  targetPrice: number;
  markupPercent: number;
  discountPercent: number;
}

/**
 * Calculates weekend pricing suggestion based on Friday and Saturday prices
 * According to specification: target_price = (avg_friday + avg_saturday) * coefficient
 * markup_percent = ((target_price / avg_saturday) - 1) * 100
 */
export function calculateWeekendPricingSuggestion(
  batchData: {
    days?: any[];
    minAvailability?: any;
  },
  coefficient: number,
  checkInDate: string
): WeekendPricingSuggestion | null {
  if (!batchData.days || batchData.days.length < 2) {
    console.warn("❌ Insufficient days data for weekend pricing calculation");
    return null;
  }

  const checkInDateObj = new Date(checkInDate);
  const fridayDateObj = new Date(checkInDateObj);
  fridayDateObj.setDate(fridayDateObj.getDate() - 1); // Day before check-in

  const fridayDateStr = fridayDateObj.toISOString().split("T")[0];
  const saturdayDateStr = checkInDateObj.toISOString().split("T")[0]; // Naprawiono format daty

  console.log("🔍 Looking for pricing data:", {
    fridayDateStr,
    saturdayDateStr,
    availableDays: batchData.days.map((d) => d.date),
    batchDataStructure: {
      hasDays: !!batchData.days,
      daysCount: batchData.days?.length,
      firstDayStructure: batchData.days?.[0]
        ? {
            date: batchData.days[0].date,
            hasRooms: !!batchData.days[0].rooms,
            roomsType: Array.isArray(batchData.days[0].rooms)
              ? "array"
              : typeof batchData.days[0].rooms,
            roomsCount: Array.isArray(batchData.days[0].rooms)
              ? batchData.days[0].rooms.length
              : Object.keys(batchData.days[0].rooms || {}).length,
            firstRoomSample: Array.isArray(batchData.days[0].rooms)
              ? batchData.days[0].rooms[0]
              : Object.values(batchData.days[0].rooms || {})[0],
          }
        : null,
    },
  });

  // Find Friday and Saturday data
  const fridayData = batchData.days.find((day) => day.date === fridayDateStr);
  const saturdayData = batchData.days.find(
    (day) => day.date === saturdayDateStr
  );

  if (!fridayData || !saturdayData) {
    console.warn("❌ Missing Friday or Saturday data for weekend pricing", {
      hasFridayData: !!fridayData,
      hasSaturdayData: !!saturdayData,
    });
    return null;
  }

  // Calculate average prices for Friday
  const fridayPrices = extractRoomPrices(fridayData.rooms, fridayDateStr);
  const avgFridayPrice = calculateAveragePrice(fridayPrices);

  // Calculate average prices for Saturday
  const saturdayPrices = extractRoomPrices(saturdayData.rooms, saturdayDateStr);
  const avgSaturdayPrice = calculateAveragePrice(saturdayPrices);

  console.log("💰 Price extraction results:", {
    fridayPrices,
    saturdayPrices,
    avgFridayPrice,
    avgSaturdayPrice,
  });

  if (avgFridayPrice === 0 || avgSaturdayPrice === 0) {
    console.warn("❌ Zero average price detected", {
      avgFridayPrice,
      avgSaturdayPrice,
      fridayPricesCount: fridayPrices.length,
      saturdayPricesCount: saturdayPrices.length,
    });
    return null;
  }

  // Calculate target price and markup according to specification
  const targetPrice = (avgFridayPrice + avgSaturdayPrice) * coefficient;
  const markupPercent = (targetPrice / avgSaturdayPrice - 1) * 100;
  // discountPercent should have the same value as markupPercent (with sign)
  // Negative value = discount, positive value = markup
  const discountPercent = markupPercent;

  console.log("✅ Weekend pricing calculation successful:", {
    avgFridayPrice,
    avgSaturdayPrice,
    coefficient,
    targetPrice,
    markupPercent,
    discountPercent,
    calculation: `targetPrice = (${avgFridayPrice} + ${avgSaturdayPrice}) * ${coefficient} = ${targetPrice}`,
    markupCalculation: `markupPercent = (${targetPrice} / ${avgSaturdayPrice} - 1) * 100 = ${markupPercent}%`,
  });

  return {
    avgFridayPrice: Math.round(avgFridayPrice),
    avgSaturdayPrice: Math.round(avgSaturdayPrice),
    targetPrice: Math.round(targetPrice),
    markupPercent: Math.round(markupPercent * 100) / 100, // Round to 2 decimals
    discountPercent: Math.round(discountPercent * 100) / 100, // Round to 2 decimals
  };
}

/**
 * Extracts room prices from rooms data - handles both test structure and real API structure
 */
function extractRoomPrices(rooms: any, dateStr: string): number[] {
  const prices: number[] = [];

  // Handle real API structure: rooms is an array
  if (Array.isArray(rooms)) {
    rooms.forEach((room: any) => {
      if (room.pricing) {
        // Real API: pricing is directly for this day, not indexed by date
        if (room.pricing.double?.net) {
          prices.push(room.pricing.double.net);
        } else if (room.pricing.single?.net) {
          prices.push(room.pricing.single.net);
        } else if (room.pricing.triple?.net) {
          prices.push(room.pricing.triple.net);
        }
      }
    });
    return prices;
  }

  // Handle test structure: rooms is an object with room IDs as keys
  if (typeof rooms === "object" && rooms !== null) {
    Object.values(rooms).forEach((room: any) => {
      if (room.pricing && room.pricing[dateStr]) {
        const dayPricing = room.pricing[dateStr];

        // Prioritize double room prices, fallback to single, then triple
        if (dayPricing.double?.net) {
          prices.push(dayPricing.double.net);
        } else if (dayPricing.single?.net) {
          prices.push(dayPricing.single.net);
        } else if (dayPricing.triple?.net) {
          prices.push(dayPricing.triple.net);
        }
      }
    });
  }

  return prices;
}

/**
 * Calculates average price from array of prices
 */
function calculateAveragePrice(prices: number[]): number {
  if (prices.length === 0) return 0;

  const sum = prices.reduce((total, price) => total + price, 0);
  return sum / prices.length;
}
